import {
  Box,
  FormControl,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import React from 'react';

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const RefundFormSummary = ({
  refundAmount,
  refundPercentage,
  error,
  onRefundAmountChange,
  ticket,
}) => {
  const currencySymbol = getSymbolFromCurrency(
    ticket?.contact?.store?.country?.currency?.code
  );

  // `refundsSummary` porte la somme des remboursements REELLEMENT executes.
  // `order.amountRefunded` est un compteur cumule qui peut compter plusieurs
  // fois le meme remboursement : on ne s'en sert qu'a defaut, quand un backend
  // plus ancien ne renvoie pas la synthese.
  const summary = ticket?.order?.refundsSummary || null;

  const orderPrice = round2(summary?.orderPrice ?? ticket?.order?.price);
  const refundedTotal = round2(
    summary ? summary.refundedTotal : ticket?.order?.amountRefunded
  );
  const refundedCount = Number(summary?.refundedCount) || 0;
  const pendingTotal = round2(summary?.pendingTotal);
  const pendingCount = Number(summary?.pendingCount) || 0;
  const counterMismatch = Boolean(summary?.counterMismatch);

  // Plafond de saisie : prix TTC de la commande moins ce qui a deja ete
  // rembourse. Borne basse a 0, pour ne jamais proposer un montant negatif.
  const maxRefundAmount = summary
    ? Math.max(0, round2(summary.maxRefundable))
    : Math.max(0, round2(orderPrice - refundedTotal));

  const isFullyRefunded = orderPrice > 0 && refundedTotal === orderPrice;
  const isInconsistent = orderPrice > 0 && refundedTotal > orderPrice;
  const refundedPercentage =
    orderPrice > 0 ? (refundedTotal / orderPrice) * 100 : 0;

  const handleRefundAmountChange = (e) => {
    let value = e.target.value; // Récupère la valeur en tant que chaîne de caractères

    // Supprimer les espaces et les zéros au début
    value = value.replace(/^0+/, '') || '0'; // Si la valeur est vide après suppression, mettre 0

    // Si la valeur n'est pas numérique, on réinitialise à 0
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      value = '0';
    } else {
      // Limiter la valeur à "maxRefundAmount"
      if (numericValue < 0) value = '0';
      else if (numericValue > maxRefundAmount)
        value = maxRefundAmount.toString();
    }

    // On met à jour le state avec la nouvelle valeur
    onRefundAmountChange(value);
  };

  const isError = Boolean(error); // Vérification si une erreur existe

  let plafondNotice = '';
  if (isFullyRefunded) {
    plafondNotice = `Commande déjà remboursée en totalité (${orderPrice} ${currencySymbol}).`;
  } else if (isInconsistent) {
    plafondNotice =
      `Donnée incohérente : ${refundedTotal} ${currencySymbol} remboursés ` +
      `pour une commande de ${orderPrice} ${currencySymbol}. Le plafond ne peut pas être calculé — ` +
      `signalez cette commande avant de saisir un montant.`;
  }
  const helperText = error || plafondNotice; // Erreur du formulaire, sinon explication du plafond

  let amountLabel = `Montant de la remise - max : ${maxRefundAmount}`;
  if (isInconsistent) {
    amountLabel = 'Montant de la remise - plafond indisponible';
  } else if (isFullyRefunded) {
    amountLabel = 'Montant de la remise - commande déjà remboursée';
  }

  const showRefundRecap =
    refundedTotal > 0 || pendingTotal > 0 || counterMismatch;

  return (
    <Box>
      {/* Ce qui a deja ete rembourse sur la commande, avant toute saisie :
          l'agent doit voir sur quoi le plafond est calcule. */}
      {showRefundRecap && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          {refundedTotal > 0 && (
            <Typography variant="body2">
              Déjà remboursé :{' '}
              <strong>
                {refundedTotal} {currencySymbol}
              </strong>{' '}
              sur {orderPrice} {currencySymbol}
              {refundedCount > 0 &&
                ` — ${refundedCount} remboursement${
                  refundedCount > 1 ? 's' : ''
                }`}
            </Typography>
          )}
          {pendingTotal > 0 && (
            <Typography variant="body2" color="warning.main">
              {pendingTotal} {currencySymbol} en attente de validation (
              {pendingCount} demande{pendingCount > 1 ? 's' : ''})
            </Typography>
          )}
          {counterMismatch && (
            <Typography variant="caption" color="warning.main" component="p">
              Le compteur de la commande indique {summary?.counterValue}{' '}
              {currencySymbol} : écart avec les remboursements réellement
              exécutés, à signaler.
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '69% 29%',
          gap: 2,
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        {/* Montant de la remise */}
        <FormControl variant="standard" sx={{ width: '100%' }}>
          <TextField
            variant="outlined"
            label={amountLabel}
            size="small"
            value={refundAmount}
            type="number"
            onChange={handleRefundAmountChange}
            // Applique l'état d'erreur si nécessaire
            error={isError}
            helperText={helperText} // Affiche le texte d'erreur sous le champ
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    {currencySymbol}
                  </InputAdornment>
                ),
              },
            }}
          />
        </FormControl>

        {/* Affichage du pourcentage actuel et du pourcentage déjà remboursé */}
        <FormControl variant="standard" sx={{ width: '100%' }}>
          <TextField
            variant="outlined"
            label="% Actuel | % Déjà demandé"
            size="small"
            disabled
            value={
              (refundPercentage
                ? `${refundPercentage.toFixed(3)}%`
                : '0.000%') + ` | ${refundedPercentage.toFixed(3)}%`
            }
          />
        </FormControl>
      </Box>
    </Box>
  );
};
